import { CheckCircleOutline, Email, VpnKey } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Sidebar from "../components/Layout/Sidebar";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const AccountActivation: React.FC = () => {
  const { user, refreshUser, loading } = useAuth();
  const navigate = useNavigate();
  const [registrationCode, setRegistrationCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Redirect to login if not authenticated
  if (!loading && !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has expired access (trial or subscription)
  const isTrialExpired =
    user?.is_access_expired || user?.is_trial_expired || false;

  // Determine if it's an expired trial vs expired full access
  const isExpiredTrial = user?.account_status === "TRIAL" && isTrialExpired;
  const isExpiredFullAccess =
    user?.account_status === "FULL_ACCESS" && user?.is_access_expired;

  // Redirect to base path if user doesn't need activation
  // (not pending activation and not expired)
  if (
    !loading &&
    user &&
    user.account_status !== "PENDING_ACTIVATION" &&
    !isTrialExpired &&
    !user.is_access_expired
  ) {
    return <Navigate to="/" replace />;
  }

  const handleActivateWithCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!registrationCode.trim()) {
      setError("Please enter a registration code");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.activateAccount(registrationCode);

      setSuccess(response.message || "Account activated successfully!");

      // Refresh user data - this will trigger automatic redirect
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "Failed to activate account. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        backgroundColor: "background.default",
      }}
    >
      {/* Sidebar */}
      <Sidebar hideMenuItems />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          overflow: "auto",
          pt: 6,
          pb: 6,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "lg",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 4,
          }}
        >
          {/* Logo and Header */}
          <Box
            sx={{
              mb: 4,
              textAlign: "center",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src="/aa_logo_highres_white_900.png"
              alt="Alpha Affinity Logo"
              style={{
                height: "120px",
                width: "auto",
                objectFit: "contain",
                marginBottom: "24px",
              }}
            />
            <Typography
              component="h1"
              variant="h3"
              align="center"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {isExpiredFullAccess
                ? "Your Subscription Has Expired"
                : isExpiredTrial
                  ? "Your Trial Has Expired"
                  : "Welcome to Alpha Affinity"}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 4, maxWidth: 600, mt: 2 }}
            >
              {isExpiredFullAccess
                ? "Your subscription has expired. Enter a new activation code or schedule a follow-up call to renew your access to the Market Graph Platform."
                : isExpiredTrial
                  ? "Your trial period has ended. Enter an activation code or schedule a follow-up call to continue using the Market Graph Platform."
                  : "Choose one of the options below to unlock full platform access"}
            </Typography>
          </Box>

          {/* Success State */}
          {success ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "2px solid",
                borderColor: "success.main",
                background: "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
                boxShadow: "none",
                maxWidth: 600,
                width: "100%",
                textAlign: "center",
              }}
            >
              <CardContent sx={{ p: 5 }}>
                <Box
                  sx={{
                    backgroundColor: "rgba(46, 125, 50, 0.2)",
                    borderRadius: "50%",
                    width: 80,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    mb: 3,
                  }}
                >
                  <CheckCircleOutline
                    sx={{ fontSize: 48, color: "success.main" }}
                  />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Account Activated!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {success}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Redirecting you to the platform...
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, width: "100%", maxWidth: 800 }}
                >
                  {error}
                </Alert>
              )}

              {/* Options Cards */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                  width: "100%",
                  maxWidth: 1000,
                  mb: 4,
                }}
              >
                {/* Option 1: Registration Code Card */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background:
                      "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
                    boxShadow: "none",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(25, 118, 210, 0.2)",
                          borderRadius: "50%",
                          p: 1.5,
                          mr: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <VpnKey sx={{ fontSize: 28, color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          Option 1
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          Activation Code
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {isExpiredFullAccess
                        ? "Have an activation code? Enter it below to renew your subscription and regain full access to the platform."
                        : isExpiredTrial
                          ? "Have an activation code? Enter it below to continue your access to the platform."
                          : "Have an activation code? Enter it below to instantly activate your account and start exploring the platform."}
                    </Typography>

                    <Box component="form" onSubmit={handleActivateWithCode}>
                      <TextField
                        fullWidth
                        id="activationCode"
                        label="Enter Activation Code"
                        name="activationCode"
                        autoComplete="off"
                        placeholder="e.g., TRIAL2025"
                        value={registrationCode}
                        onChange={(e) => setRegistrationCode(e.target.value)}
                        disabled={isLoading}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                          },
                        }}
                      />
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: "none",
                          fontSize: "1rem",
                        }}
                      >
                        {isLoading ? "Activating..." : "Activate Account"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {/* Option 2: Contact Us Card */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background:
                      "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
                    boxShadow: "none",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(25, 118, 210, 0.2)",
                          borderRadius: "50%",
                          p: 1.5,
                          mr: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Email sx={{ fontSize: 28, color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          Option 2
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {isExpiredFullAccess
                            ? "Continue Your Access"
                            : isExpiredTrial
                              ? "Continue Your Access"
                              : "Get a Free Trial"}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {isExpiredFullAccess
                        ? "Don't have a code? Schedule a follow-up call with our team to discuss your continued access and explore the full potential of the platform."
                        : isExpiredTrial
                          ? "Don't have a code? Schedule a follow-up call with our team to discuss your continued access and explore the full potential of the platform."
                          : "Don't have a code? Schedule a personalized onboarding call with our team to receive free trial access and get started with the platform."}
                    </Typography>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        backgroundColor: "rgba(25, 118, 210, 0.1)",
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Contact us at:
                      </Typography>
                      <Typography
                        variant="h6"
                        component="a"
                        href="mailto:support@alpha-affinity.com"
                        sx={{
                          color: "primary.main",
                          textDecoration: "none",
                          fontWeight: 600,
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        support@alpha-affinity.com
                      </Typography>
                    </Paper>

                    <Typography variant="body2" color="text.secondary">
                      {isExpiredFullAccess
                        ? "We typically respond within 24 hours to schedule your follow-up call and discuss your access options."
                        : isExpiredTrial
                          ? "We typically respond within 24 hours to schedule your follow-up call and discuss your access options."
                          : "We typically respond within 24 hours to schedule your onboarding session and provide your free trial access."}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountActivation;
